import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { IMAGES } from "../../data/site";
import { useContent } from "../../i18n/content";
import { MaskedLine, Reveal, EASE } from "../motion/Reveal";
import { scrollToSection } from "../Navbar";

const ENERGY_LINES = [
    { left: "18%", delay: "0s", duration: "4.2s" },
    { left: "42%", delay: "1.1s", duration: "3.4s" },
    { left: "71%", delay: "0.6s", duration: "5s" },
    { left: "88%", delay: "1.8s", duration: "3.8s" },
];

export const Hero = () => {
    const ref = useRef(null);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { ui } = useContent();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="home"
            data-testid="hero-section"
            ref={ref}
            className="relative min-h-screen flex flex-col justify-end overflow-hidden"
        >
            <motion.div className="absolute inset-0" style={{ y: imgY }}>
                <div className="absolute inset-0 kenburns">
                    <img
                        src={IMAGES.hero}
                        alt={ui.hero.imgAlt}
                        className="w-full h-full object-cover"
                        loading="eager"
                    />
                </div>
                <div className="absolute inset-0 bg-[#292a24]/60" />
                <div className="absolute inset-0 hero-grid" />
                {ENERGY_LINES.map((l, i) => (
                    <span
                        key={i}
                        aria-hidden="true"
                        className="energy-line absolute top-0 bottom-0 w-px bg-[#f6c422] blur-[1px]"
                        style={{ left: l.left, animationDelay: l.delay, animationDuration: l.duration }}
                    />
                ))}
                <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#292a24] to-transparent" />
            </motion.div>

            <motion.div
                style={{ y: textY, opacity: fade }}
                className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pb-16 pt-44"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className="overline-text mb-8 flex items-center gap-4"
                >
                    <span className="h-px w-12 bg-[#f6c422]" aria-hidden="true" />
                    {ui.hero.overline}
                </motion.p>

                <h1 className="font-display font-extrabold uppercase leading-[0.98] tracking-tight text-[13vw] sm:text-6xl lg:text-7xl xl:text-[86px] text-white max-w-5xl">
                    <MaskedLine delay={0.35}>{ui.hero.lines[0]}</MaskedLine>
                    <MaskedLine delay={0.5}>{ui.hero.lines[1]}</MaskedLine>
                    <MaskedLine delay={0.65} className="text-[#f6c422]">
                        {ui.hero.lines[2]}
                    </MaskedLine>
                </h1>

                <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
                    <Reveal delay={0.85} className="max-w-xl">
                        <p className="text-[#d8d9d2] text-base lg:text-lg leading-relaxed">
                            {ui.hero.sub}
                        </p>
                        <p className="overline-text mt-5">{ui.hero.tagline}</p>
                    </Reveal>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1, duration: 0.8, ease: EASE }}
                        className="flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-cta-explore-group"
                            onClick={() => scrollToSection("ecosystem", navigate, pathname)}
                            className="inline-flex items-center gap-3 bg-[#f6c422] text-[#292a24] font-display font-bold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:bg-[#ffe169] transition-colors duration-300 group"
                        >
                            {ui.hero.primary}
                            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                        <button
                            data-testid="hero-cta-our-companies"
                            onClick={() => scrollToSection("companies", navigate, pathname)}
                            className="inline-flex items-center gap-3 border border-white/30 text-white font-display font-semibold text-xs tracking-[0.18em] uppercase px-8 py-4 hover:border-[#f6c422] hover:text-[#f6c422] transition-colors duration-300 backdrop-blur-sm"
                        >
                            {ui.hero.secondary}
                        </button>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.3, duration: 1 }}
                    className="mt-16 pt-6 border-t border-white/15 flex items-center justify-between gap-6"
                >
                    <div className="flex gap-8 sm:gap-14">
                        {ui.hero.strip.map((t) => (
                            <span
                                key={t}
                                className="font-display text-[10px] sm:text-xs font-semibold tracking-[0.28em] uppercase text-white/50"
                            >
                                {t}
                            </span>
                        ))}
                    </div>
                    <button
                        data-testid="hero-scroll-indicator"
                        onClick={() => scrollToSection("about", navigate, pathname)}
                        className="flex items-center gap-3 text-white/50 hover:text-[#f6c422] transition-colors duration-300"
                        aria-label="Scroll to About section"
                    >
                        <span className="hidden sm:block font-display text-[10px] tracking-[0.3em] uppercase">{ui.hero.scroll}</span>
                        <motion.span
                            animate={{ y: [0, 8, 0] }}
                            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                        >
                            <ArrowDown className="w-4 h-4" />
                        </motion.span>
                    </button>
                </motion.div>
            </motion.div>
        </section>
    );
};
